import {
  Box,
  IconButton,
  Alert as MuiAlert,
  Stack,
  stackClasses as muiStackClasses,
  alertClasses as muiAlertClasses,
  alertTitleClasses,
  useThemeProps,
  type AlertProps as MuiAlertProps,
  capitalize,
} from '@mui/material';
import {
  ComponentsProps,
  ComponentsVariants,
  styled,
} from '@mui/material/styles';
import * as React from 'react';
import clsx from 'clsx';
import { useUnifyElevationProp } from 'src/styles/elevationUtils';
import {
  AlertBaseContextProvider,
  defaultAlertBaseContextValue,
  IAlertBaseContext,
} from 'src/components/AlertBase/AlertBaseContext';
import alertBaseClasses, {
  AlertBaseClassKey,
  getAlertBaseUtilityClass,
} from 'src/components/AlertBase/alertBaseClasses';
import { getAlertBaseStyledOverridesResolver } from 'src/components/AlertBase/styledAlertBaseOverridesResolver';
import UnifyIcons from '../UnifyIcons';

declare module '@mui/material/styles' {
  interface ComponentNameToClassKey {
    UnifyAlertBase: AlertBaseClassKey;
  }

  interface ComponentsPropsList {
    UnifyAlertBase: AlertBaseProps;
  }

  interface Components {
    UnifyAlertBase?: {
      defaultProps?: ComponentsProps['UnifyAlertBase'];
      variants?: ComponentsVariants['UnifyAlertBase'];
    };
  }
}

/**
 * @access package
 */
export const alertBaseHorizontalPaddingInThemeSpacing = 2;
/**
 * @access package
 */
export const alertBaseVerticalPaddingInThemeSpacing = 1;

// Used for the default icon mapping of Toast and Banner components
const defaultIconMapping = {
  success: <UnifyIcons.CheckCircleFilled fontSize="inherit" />,
  warning: <UnifyIcons.WarningFilled fontSize="inherit" />,
  error: <UnifyIcons.DoNotDisturbOnFilled fontSize="inherit" />,
  info: <UnifyIcons.InfoFilled fontSize="inherit" />,
};

/**
 * Props that we have added that the MUI component does not support.
 */
type AlertBaseOwnProps = IAlertBaseContext & {
  /**
   * Set to true to remove the icon.
   *
   * @default true
   */
  showIcon?: boolean;
};

type AlertBaseBlockedProps = keyof Pick<
  MuiAlertProps,
  'color' | 'square' | 'variant'
>;

/**
 * @access package
 */
export type AlertBaseProps = Omit<MuiAlertProps, AlertBaseBlockedProps> &
  AlertBaseOwnProps;

/**
 * Styled component with shared styles for all components that render an AlertBase.
 */
const UnifyAlertBaseRoot = styled(MuiAlert, {
  name: 'UnifyAlertBase',
  slot: 'Root',
  overridesResolver: getAlertBaseStyledOverridesResolver('root'),
})<
  Omit<AlertBaseProps, 'closeText'> & {
    closeText: string;
    ownerState: Required<AlertBaseOwnProps>;
  }
>(({ theme, action, onClose, ownerState }) => {
  const { actionPlacement, showIcon } = ownerState;
  const iconHeightInPx = 20;

  return {
    backgroundColor: 'unset',
    color: 'inherit',
    flexWrap: actionPlacement === 'inline' ? 'nowrap' : 'wrap',
    // Only one unit of vertical spacing because there is one unit on the top/bottom of the message slot.
    // This gives us a total of 2 units that matches Figma.
    padding: theme.spacing(
      alertBaseVerticalPaddingInThemeSpacing,
      alertBaseHorizontalPaddingInThemeSpacing
    ),
    // The body2 line-height results in an intrinsic height of 19px for the message instead of 20px like the icon
    // So we need to increase the line-height to ensure that a single-line alert message is perfectly
    // vertically-centered with its adjacent icon / close button.
    lineHeight: `${iconHeightInPx}px`,

    [`.${muiAlertClasses.message}`]: {
      padding: theme.spacing(alertBaseVerticalPaddingInThemeSpacing, 0),
      flexGrow: 1,
      ...(showIcon && {
        flexBasis: 0,
      }),
      ...(!showIcon &&
        actionPlacement === 'footer' &&
        onClose && {
          flexBasis: 0,
        }),
    },
    [`.${muiAlertClasses.icon}`]: {
      opacity: 1,
      color: 'inherit',
      fontSize: iconHeightInPx,
      padding: theme.spacing(alertBaseVerticalPaddingInThemeSpacing, 0),
      marginRight: theme.spacing(1),
    },
    // Align line height and margin with Figma, align message content with actions
    [`.${alertTitleClasses.root}`]: {
      lineHeight: theme.spacing(3),
      marginBottom: theme.spacing(1),
    },

    // --------- Actions when there is no title --------- //
    [`.${muiAlertClasses.message}:not(:has(.${alertTitleClasses.root})) ~ .${muiAlertClasses.action}`]:
      {
        alignItems: 'flex-end',

        // The nested stack that is present when a close button AND actions are present
        [`.${muiStackClasses.root}`]: {
          // Render close button on same line as actions when there is no title and actionPlacement is inline
          ...(actionPlacement === 'inline' && {
            paddingTop: 0,
            flexDirection: 'row-reverse',
            alignItems: 'flex-start',
          }),
          // Render close button stacked vertically above the actions when there is no title and actionPlacement is footer
          ...(actionPlacement === 'footer' && {
            paddingTop: 2, // Vertically center the close button with the alert message / icon
            flexDirection: 'column',
            alignItems: 'flex-end',
          }),
        },
      },

    // --------- Actions when there is a title --------- //
    [`.${muiAlertClasses.message}:has(.${alertTitleClasses.root}) ~ .${muiAlertClasses.action}`]:
      {
        // Render actions at the bottom of the alert when there is no close button
        ...(!onClose && {
          alignItems: 'flex-end',
        }),
      },

    // --------- Actions regardless of whether there is a title --------- //
    [`.${muiAlertClasses.action}`]: {
      paddingTop: 3,

      ...(actionPlacement === 'inline' && {
        ...((action || !onClose) && {
          flexShrink: 0,
        }),
      }),

      /**
       * Styling logic for footer actions.
       * Note that we are rendering both onClose and actions within the MuiAlert-action element,
       * which is rendered at the end (right side) of the MuiAlert-root flex container by default.
       *
       * If there is a footer action and an onClose, we want the onClose to be horizontally aligned with the alert content,
       * but want the footer action to wrap below the main content.
       * Setting display: contents on the Alert action element makes it render the onClose and action elements as children of its parent (the Alert root),
       * which lets us keep the onClose on the same line as the alert content, while wrapping the footer action down below.
       *
       * If there is not an onClose, we can just make the alert action wrap to the next line by setting flex-basis: 100%.
       */
      ...(actionPlacement === 'footer' &&
        (onClose
          ? {
              display: 'contents',
            }
          : {
              flexBasis: '100%',
              justifyContent: 'flex-end',
            })),
    },
  };
});

/**
 * @access package
 */
export const AlertBase = React.forwardRef<HTMLDivElement, AlertBaseProps>(
  function AlertBase(inProps, forwardedRef) {
    const props = useThemeProps({
      props: inProps,
      name: 'UnifyAlertBase',
    });

    const {
      children,
      className: classNameProp,
      closeText = 'Close',
      actionPlacement = 'inline',
      action,
      onClose,
      // eslint-disable-next-line deprecation/deprecation
      elevation: unifyElevation = 0,
      showIcon = true,
      icon: iconIn,
      iconMapping: iconMappingIn,
      ...rest
    } = props;

    const muiElevation = useUnifyElevationProp(unifyElevation);

    const icon = iconIn ?? (showIcon ? undefined : false);
    const iconMapping = iconMappingIn ?? defaultIconMapping;

    const contextValue = {
      ...defaultAlertBaseContextValue,
      actionPlacement,
      severity:
        // eslint-disable-next-line deprecation/deprecation
        rest.severity ?? defaultAlertBaseContextValue.severity,
    } as const;

    const className = clsx(
      classNameProp,
      alertBaseClasses.root,
      getAlertBaseUtilityClass(`severity${capitalize(contextValue.severity)}`)
    );

    let actionToRender = action;
    if (onClose && action) {
      const closeButton = (
        <IconButton
          size="small"
          aria-label={closeText}
          color="inherit"
          onClick={onClose}
          sx={{
            // Add additional space between actions if they exist.
            ml: action && actionPlacement === 'inline' ? 1 : 0,
          }}
        >
          <UnifyIcons.Close fontSize={'small'} data-testid={'CloseIcon'} />
        </IconButton>
      );

      if (actionPlacement === 'inline') {
        actionToRender = (
          <Stack
            alignItems={'flex-end'}
            justifyContent={'space-between'}
            height={'100%'}
          >
            {closeButton}
            <div>{action}</div>
          </Stack>
        );
      } else {
        actionToRender = (
          <>
            <Stack height={'100%'} paddingTop={0.5} marginRight={-1}>
              {closeButton}
            </Stack>
            <Box
              sx={{
                flexBasis: '100%', // Make footer action wrap to next line
                justifyContent: 'flex-end',
                display: 'flex',
              }}
            >
              <Box
                sx={{
                  marginRight: -1, // Match MUI alert action margin-right
                }}
              >
                {action}
              </Box>
            </Box>
          </>
        );
      }
    }

    return (
      <AlertBaseContextProvider value={contextValue}>
        <UnifyAlertBaseRoot
          ref={forwardedRef}
          className={className}
          action={actionToRender}
          closeText={closeText}
          onClose={onClose}
          elevation={muiElevation}
          icon={icon}
          iconMapping={iconMapping}
          ownerState={{
            actionPlacement,
            showIcon,
            severity: contextValue.severity,
          }}
          {...rest}
        >
          {children}
        </UnifyAlertBaseRoot>
      </AlertBaseContextProvider>
    );
  }
);

export default AlertBase;
